import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { Pencil, Trash2, Search, Plus, FileText } from "lucide-react";
import { ExpenseCategoryFilters } from "@/components/categories/ExpenseCategoryFilters";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ExpenseCategoryDetails from "@/pages/ExpenseCategoryDetails";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { IconPicker } from "@/components/categories/IconPicker";
import * as Icons from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const categorySchema = z.object({
  id: z.string().optional(),
  nome: z.string().min(1, "Nome é obrigatório"),
  status: z.enum(["ativo", "inativo"]).default("ativo"),
  cor_hex: z.string().optional(),
  icone: z.string().optional(),
  descricao: z.string().optional(),
});

type CategoryForm = z.infer<typeof categorySchema>;

type ExpenseCategory = {
  id: string;
  nome: string;
  status: string;
  cor_hex?: string | null;
  icone?: string | null;
  descricao?: string | null;
  created_at?: string | null;
};

type ExpenseCategoryFilterState = {
  searchTerm: string;
  status: string[];
};

export function ExpenseCategoriesTab() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState<ExpenseCategoryFilterState>({
    searchTerm: "",
    status: [],
  });
  const [selectedIcon, setSelectedIcon] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("#000000");
  const [expenseCategoryProfile, setExpenseCategoryProfile] = useState<{ id: string; name: string } | null>(null);

  const form = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    defaultValues: { nome: "", status: "ativo", cor_hex: "#000000", icone: "", descricao: "" },
  });

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["expense-categories", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("id, nome, status, cor_hex, icone, descricao, created_at")
        .eq("tenant_id", tenantId as string)
        .order("nome");
      if (error) throw error;
      return (data || []) as ExpenseCategory[];
    },
  });

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    categories?.forEach((c) => {
      if (c.status) statuses.add(c.status);
    });
    return Array.from(statuses).sort();
  }, [categories]);

  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
    };

    if (!categories) return counts;

    categories.forEach((category) => {
      const matchesOtherFilters = (exceptFilter: string) => {
        if (exceptFilter === "status") {
          return (
            (!filters.searchTerm || category.nome.toLowerCase().includes(filters.searchTerm.toLowerCase()))
          );
        }
        return true;
      };

      if (matchesOtherFilters("status") && category.status) {
        counts.status[category.status] = (counts.status[category.status] || 0) + 1;
      }
    });

    return counts;
  }, [categories, filters]);

  const filteredCategories = useMemo(() => {
    let filtered = categories;

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter((c) => c.nome.toLowerCase().includes(searchLower));
    }

    if (filters.status.length > 0) {
      filtered = filtered.filter((c) => filters.status.includes(c.status));
    }

    return filtered;
  }, [categories, filters]);

  const handleFiltersChange = (newFilters: ExpenseCategoryFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCategories.slice(start, start + itemsPerPage);
  }, [filteredCategories, currentPage, itemsPerPage]);

  const upsertMutation = useMutation({
    mutationFn: async ({ id, nome, status, cor_hex, icone, descricao }: CategoryForm) => {
      if (!tenantId) throw new Error("Tenant não encontrado");
      const categoryData = {
        nome,
        status: status || "ativo",
        cor_hex: selectedColor || cor_hex || null,
        icone: selectedIcon || icone || null,
        descricao: descricao || null,
      };
      if (id) {
        const { error } = await supabase
          .from("expense_categories")
          .update(categoryData)
          .eq("id", id);
        if (error) throw error;
        return { id, ...categoryData };
      }
      const { data, error } = await supabase
        .from("expense_categories")
        .insert([{ ...categoryData, tenant_id: tenantId }])
        .select("id, nome, status, cor_hex, icone, descricao")
        .single();
      if (error) {
        // tratar duplicidade por nome
        const { data: existing } = await supabase
          .from("expense_categories")
          .select("id, nome, status, cor_hex, icone, descricao")
          .eq("tenant_id", tenantId)
          .eq("nome", nome)
          .maybeSingle();
        if (existing) return existing;
        throw error;
      }
      return data as any;
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories", tenantId] });
      toast.success(editingId ? "Categoria atualizada" : "Categoria cadastrada");
      if (editingId) {
        void logCrud({ entitySlug: "entity:expense_categories", operationSlug: "update", entityId: editingId, entityName: form.getValues("nome"), screenSlug: "categorias-despesa", before: { id: editingId }, after: result });
      } else if (result?.id) {
        void logCrud({ entitySlug: "entity:expense_categories", operationSlug: "create", entityId: result.id, entityName: result.nome, screenSlug: "categorias-despesa" });
      }
      form.reset({ id: undefined, nome: "", status: "ativo", cor_hex: "#000000", icone: "", descricao: "" });
      setSelectedIcon("");
      setSelectedColor("#000000");
      setEditingId(null);
      setOpen(false);
    },
    onError: (e: any) => toast.error("Erro ao salvar: " + (e?.message || "")),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { error } = await supabase
        .from("expense_categories")
        .update({ status: newStatus })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories", tenantId] });
      toast.success("Status alterado com sucesso");
      void logCrud({ entitySlug: "entity:expense_categories", operationSlug: "update", entityId: variables.id, screenSlug: "categorias-despesa", before: { status: variables.newStatus === 'ativo' ? 'inativo' : 'ativo' }, after: { status: variables.newStatus } });
    },
    onError: () => toast.error("Erro ao alterar status"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("expense_categories")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories", tenantId] });
      toast.success("Categoria removida");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:expense_categories", operationSlug: "delete", entityId: deleteId, screenSlug: "categorias-despesa" });
      }
      setDeleteId(null);
    },
    onError: (e: any) => {
      const msg: string = e?.message || "";
      if (msg.includes("23503") || msg.toLowerCase().includes("foreign key")) {
        toast.error("Não é possível excluir: a categoria está em uso em uma ou mais despesas.");
      } else {
        toast.error("Erro ao remover: " + msg);
      }
      setDeleteId(null);
    },
  });

  const handleOpenDialog = (category?: ExpenseCategory) => {
    if (category) {
      setEditingId(category.id);
      setSelectedIcon(category.icone || "");
      setSelectedColor(category.cor_hex || "#000000");
      form.reset({
        id: category.id,
        nome: category.nome,
        status: (category.status || "ativo") as "ativo" | "inativo",
        cor_hex: category.cor_hex || "#000000",
        icone: category.icone || "",
        descricao: category.descricao || "",
      });
    } else {
      setEditingId(null);
      setSelectedIcon("");
      setSelectedColor("#000000");
      form.reset({ id: undefined, nome: "", status: "ativo", cor_hex: "#000000", icone: "", descricao: "" });
    }
    setOpen(true);
  };

  const renderIcon = (iconName: string | null | undefined) => {
    if (!iconName) return null;
    const IconComponent = (Icons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent className="h-4 w-4" />;
  };

  const handleSubmit = (data: CategoryForm) => {
    upsertMutation.mutate(data);
  };

  return (
    <div className="space-y-6" data-cy="expense-categories-tab">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Categorias de Despesas</h2>
          <p className="text-muted-foreground">Gerencie as categorias das suas despesas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto" onClick={() => handleOpenDialog()} data-cy="new-expense-category-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            <div className="flex max-h-[90vh] flex-col">
              <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
                <DialogTitle>{editingId ? "Editar Categoria" : "Nova Categoria de Despesa"}</DialogTitle>
                <DialogDescription>
                  Preencha os dados da categoria
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <Form {...form}>
                  <form id="expense-category-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="nome"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome da Categoria *</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: Alimentação, Transporte..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cor_hex">Cor da Categoria</Label>
                        <div className="flex gap-2">
                          <Input
                            id="cor_hex"
                            type="color"
                            value={selectedColor}
                            onChange={(e) => {
                              setSelectedColor(e.target.value);
                            }}
                            className="w-20 h-10"
                            data-cy="expense-category-color-input"
                          />
                          <Input
                            type="text"
                            value={selectedColor}
                            onChange={(e) => {
                              setSelectedColor(e.target.value);
                            }}
                            placeholder="#000000"
                            className="flex-1"
                          />
                        </div>
                        <input
                          type="hidden"
                          {...form.register("cor_hex")}
                          value={selectedColor}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Ícone da Categoria</Label>
                        <IconPicker
                          value={selectedIcon}
                          onValueChange={(iconName) => {
                            setSelectedIcon(iconName);
                          }}
                        />
                        <input
                          type="hidden"
                          {...form.register("icone")}
                          value={selectedIcon}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="ativo">Ativo</SelectItem>
                                <SelectItem value="inativo">Inativo</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="descricao"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Descrição da Categoria</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Descreva a categoria..."
                              rows={3}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </div>
              <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" form="expense-category-form" disabled={upsertMutation.isPending} data-cy="expense-category-submit-btn">
                  {upsertMutation.isPending ? "Salvando..." : (editingId ? "Salvar Alterações" : "Cadastrar")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card data-cy="expense-categories-table-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="expense-categories-table-title">Lista de Categorias</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => {
                      handleFiltersChange({ ...filters, searchTerm: e.target.value });
                    }}
                    className="pl-8"
                    data-cy="expense-categories-search-input"
                  />
                </div>
                <ExpenseCategoryFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  statuses={allStatuses}
                  counts={facetCounts}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8" data-cy="expense-categories-loading">Carregando...</div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="expense-categories-empty">
              {filters.searchTerm || filters.status.length > 0 ? "Nenhuma categoria encontrada" : "Nenhuma categoria cadastrada"}
            </div>
          ) : (
            <>
              <Table data-cy="expense-categories-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome / Ícone / Cor</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCategories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {category.cor_hex && (
                            <div
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: category.cor_hex }}
                            />
                          )}
                          {category.icone && renderIcon(category.icone)}
                          <span className="font-medium">{category.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {category.descricao || "-"}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newStatus = category.status === "ativo" ? "inativo" : "ativo";
                                  toggleStatusMutation.mutate({ id: category.id, newStatus });
                                }}
                                className="cursor-pointer"
                              >
                                <Badge 
                                  variant={category.status === "ativo" ? "default" : "secondary"}
                                  className={
                                    category.status === "ativo" 
                                      ? "bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer" 
                                      : category.status === "inativo" 
                                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer" 
                                      : ""
                                  }
                                >
                                  {category.status}
                                </Badge>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="whitespace-pre-line">
                              {category.status === "ativo" 
                                ? "Clique para DESATIVAR" 
                                : "Clique para ATIVAR"}
                              {'\n'}a categoria <strong>{category.nome}</strong>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider>
                          <div className="flex justify-end gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpenseCategoryProfile({ id: category.id, name: category.nome });
                                  }}
                                  className="h-10 w-10"
                                  data-cy={`view-expense-category-${category.id}`}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line">
                                Ver ficha completa e relatórios{'\n'}da categoria <strong>{category.nome}</strong>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenDialog(category)}
                                  data-cy={`edit-expense-category-${category.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line">
                                Editar categoria{'\n'}<strong>{category.nome}</strong>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600 hover:text-white hover:bg-red-500"
                                  onClick={() => setDeleteId(category.id)}
                                  data-cy={`delete-expense-category-${category.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line">
                                Excluir categoria{'\n'}<strong>{category.nome}</strong>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredCategories.length > itemsPerPage && (
                <TablePagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(filteredCategories.length / itemsPerPage)}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredCategories.length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {expenseCategoryProfile && (() => {
        const cat = (categories || []).find((x: any) => x.id === expenseCategoryProfile.id);
        const infoItems = [
          cat?.descricao ? { value: cat.descricao } : null,
          cat?.created_at ? { value: `Criada em ${new Intl.DateTimeFormat('pt-BR').format(new Date(cat.created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!expenseCategoryProfile}
            onOpenChange={() => setExpenseCategoryProfile(null)}
            imageUrl={null}
            entityTypeLabel={"CATEGORIA DE DESPESA"}
            entityName={expenseCategoryProfile.name}
            infoItems={infoItems}
            contentClassName="px-2 pb-2 pt-1"
            renderContent={({ periodRange, setLoading }) => (
              <ExpenseCategoryDetails id={expenseCategoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
    </div>
  );
}

